// Import media types from local types file (FSD entities layer)
function _instanceof(left, right) {
    if (right != null && typeof Symbol !== "undefined" && right[Symbol.hasInstance]) {
        return !!right[Symbol.hasInstance](left);
    } else {
        return left instanceof right;
    }
}
import { MediaType } from '../types/media';
export { MediaType } from '../types/media';
/**
 * 클래스명을 조건부로 결합하는 유틸리티 함수
 * @param classes 클래스명 배열 또는 문자열
 * @returns 결합된 클래스명 문자열
 */ export function cn() {
    for(var _len = arguments.length, classes = new Array(_len), _key = 0; _key < _len; _key++){
        classes[_key] = arguments[_key];
    }
    return classes.filter(Boolean).join(' ');
}
/**
 * URL을 기반으로 미디어 타입을 판별하는 함수
 * @param url 미디어 URL 문자열
 * @returns 판별된 MediaType
 */ export function getMediaTypeFromUrl(url) {
    if (!url || typeof url !== 'string') {
        return MediaType.UNKNOWN;
    }
    var lowerUrl = url.toLowerCase();
    var urlWithoutQuery = lowerUrl.split('?')[0];
    // 이미지 확장자 확인
    var imageExtensions = [
        '.jpg',
        '.jpeg',
        '.png',
        '.gif',
        '.webp',
        '.svg',
        '.bmp',
        '.tiff'
    ];
    if (imageExtensions.some(function(ext) {
        return urlWithoutQuery.endsWith(ext);
    })) {
        return MediaType.IMAGE;
    }
    // 비디오 확장자 확인 (기존 isVideo 로직 일부 활용)
    var videoExtensions = [
        '.mp4',
        '.webm',
        '.ogg',
        '.mov',
        '.m4v'
    ];
    if (videoExtensions.some(function(ext) {
        return urlWithoutQuery.endsWith(ext);
    })) {
        return MediaType.VIDEO;
    }
    // HLS 스트리밍 URL 확인 (.m3u8)
    if (urlWithoutQuery.endsWith('.m3u8') || lowerUrl.includes('/hls/') || lowerUrl.includes('m3u8')) {
        return MediaType.VIDEO;
    }
    // 비디오 플랫폼 URL 패턴 확인
    var videoPatterns = [
        'youtube.com',
        'youtu.be',
        'vimeo.com',
        'dailymotion.com',
        'vm.tiktok'
    ];
    if (videoPatterns.some(function(pattern) {
        return lowerUrl.includes(pattern);
    })) {
        return MediaType.VIDEO;
    }
    // URL에 'video' 키워드가 포함된 경우 (스트리밍 경로 등)
    if (lowerUrl.includes('video') || lowerUrl.includes('stream')) {
        return MediaType.VIDEO;
    }
    // 기본적으로 링크로 분류
    return MediaType.LINK;
}
/**
 * 파일 확장자를 기반으로 미디어 타입을 판별하는 함수
 * @param filename 파일명
 * @returns 판별된 MediaType
 */ export function getMediaTypeFromFilename(filename) {
    if (!filename || typeof filename !== 'string') {
        return MediaType.UNKNOWN;
    }
    var lowerFilename = filename.toLowerCase();
    // 이미지 확장자 확인
    var imageExtensions = [
        '.jpg',
        '.jpeg',
        '.png',
        '.gif',
        '.webp',
        '.svg',
        '.bmp',
        '.tiff'
    ];
    if (imageExtensions.some(function(ext) {
        return lowerFilename.endsWith(ext);
    })) {
        return MediaType.IMAGE;
    }
    // 비디오 확장자 확인
    var videoExtensions = [
        '.mp4',
        '.webm',
        '.ogg',
        '.mov',
        '.m4v',
        '.avi',
        '.mkv'
    ];
    if (videoExtensions.some(function(ext) {
        return lowerFilename.endsWith(ext);
    })) {
        return MediaType.VIDEO;
    }
    return MediaType.UNKNOWN;
}
/**
 * 미디어 URL이 이미지인지 확인하는 함수
 * @param url 확인할 URL
 * @returns 이미지 여부
 */ export function isImage(url) {
    return getMediaTypeFromUrl(url) === MediaType.IMAGE;
}
/**
 * 미디어 URL이 비디오인지 확인하는 함수  
 * @param url 확인할 URL
 * @returns 비디오 여부
 */ export function isVideo(url) {
    return getMediaTypeFromUrl(url) === MediaType.VIDEO;
}
/**
 * 파일이 이미지인지 확인하는 함수
 * @param file 확인할 파일
 * @returns 이미지 여부
 */ export function isImageFile(file) {
    return file.type.startsWith('image/');
}
/**
 * 파일이 비디오인지 확인하는 함수
 * @param file 확인할 파일
 * @returns 비디오 여부
 */ export function isVideoFile(file) {
    return file.type.startsWith('video/');
}
/**
 * URL을 미디어 프록시 URL로 변환하는 함수
 * @param originalUrl 원본 URL
 * @param apiBaseUrl API 베이스 URL (선택사항)
 * @returns 프록시된 미디어 URL
 */ export function getMediaProxyUrl(originalUrl, apiBaseUrl) {
    if (!originalUrl) return '';
    // 이미 프록시 URL인 경우 그대로 반환
    if (originalUrl.includes('/api/media/proxy/')) {
        return originalUrl;
    }
    // 로컬 파일 URL인 경우 그대로 반환
    if (originalUrl.startsWith('blob:') || originalUrl.startsWith('data:')) {
        return originalUrl;
    }
    var baseUrl = apiBaseUrl || (typeof window !== 'undefined' && window.location ? "".concat(window.location.protocol, "//").concat(window.location.host) : 'http://localhost:8080');
    var encodedUrl = encodeURIComponent(originalUrl);
    return "".concat(baseUrl, "/api/media/proxy?url=").concat(encodedUrl);
}
/**
 * 미디어 파일 크기를 포맷팅하는 함수
 * @param bytes 바이트 크기
 * @returns 포맷된 크기 문자열
 */ export function formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    var k = 1024;
    var sizes = [
        'Bytes',
        'KB',
        'MB',
        'GB'
    ];
    var i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}
/**
 * 미디어 파일의 dimensions을 계산하는 함수
 * @param file 미디어 파일
 * @returns Promise로 래핑된 dimensions 객체
 */ export function getMediaDimensions(file) {
    return new Promise(function(resolve, reject) {
        if (isImageFile(file)) {
            var img = new Image();
            img.onload = function() {
                resolve({
                    width: img.naturalWidth,
                    height: img.naturalHeight
                });
            };
            img.onerror = reject;
            img.src = URL.createObjectURL(file);
        } else if (isVideoFile(file)) {
            var video = document.createElement('video');
            video.onloadedmetadata = function() {
                resolve({
                    width: video.videoWidth,
                    height: video.videoHeight
                });
            };
            video.onerror = reject;
            video.src = URL.createObjectURL(file);
        } else {
            reject(new Error('Unsupported file type'));
        }
    });
}
/**
 * 파일을 base64로 변환하는 함수
 * @param file 변환할 파일
 * @returns Promise로 래핑된 base64 문자열
 */ export function fileToBase64(file) {
    return new Promise(function(resolve, reject) {
        var reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = function() {
            return resolve(reader.result);
        };
        reader.onerror = function(error) {
            return reject(error);
        };
    });
}
/**
 * 미디어 파일 URL을 API 경로로 변환하는 함수 (dataTransforms에서 이주)
 * @param path 파일 경로 또는 URL
 * @returns 처리된 미디어 파일 URL
 */ export function processMediaFileUrl(path) {
    if (!path || path.trim() === '') {
        return undefined;
    }
    // If already a full URL, return as is
    if (path.startsWith('http://') || path.startsWith('https://')) {
        return path;
    }
    // Get API_BASE_URL from environment or use default
    var API_BASE_URL = process.env['NEXT_PUBLIC_API_BASE_URL'] || 'http://localhost:8080';
    // Ensure no double slashes if API_BASE_URL ends with /
    var baseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
    // If it starts with '/', prepend API base and media path
    if (path.startsWith('/')) {
        // Ensure path doesn't already start with the endpoint prefix accidentally
        if (path.startsWith('/api/media/file/')) {
            return "".concat(baseUrl).concat(path);
        }
        return "".concat(baseUrl, "/api/media/file").concat(path);
    }
    // Treat all other non-absolute, non-root-relative paths as relative
    return "".concat(baseUrl, "/api/media/file/").concat(path);
}
/**
 * 비디오 URL 처리 함수
 * @param url 비디오 URL
 * @param baseUrl API 기본 URL (선택적)
 * @param fileBlob 파일 Blob (선택적)
 * @returns 처리된 비디오 URL
 */ export var processVideoUrl = function(url, baseUrl, fileBlob) {
    if (!url || url.trim() === '') {
        if (_instanceof(fileBlob, Blob)) {
            return URL.createObjectURL(fileBlob);
        }
        return '';
    }
    // Blob, Data URL, HTTP/HTTPS URL은 그대로 사용
    if (url.startsWith('blob:') || url.startsWith('data:') || url.startsWith('http://') || url.startsWith('https://')) {
        return url;
    }
    // 상대 경로 처리 (baseUrl이 제공된 경우만)
    if (baseUrl && (url.startsWith('/') || !url.startsWith('http') && !url.startsWith('blob:') && !url.startsWith('data:'))) {
        var relativePath = url.startsWith('/') ? url.substring(1) : url;
        var cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
        return "".concat(cleanBaseUrl, "/api/media/file/").concat(relativePath);
    }
    return url;
};
